import { motion } from "framer-motion";
import { CalendarDays, Fingerprint, Inbox, Timer } from "lucide-react";
import appCheckIn from "../../../assets/images/home/product/app-check-in.webp";
import appHome from "../../../assets/images/home/product/app-home.webp";
import appLeaveApply from "../../../assets/images/home/product/app-leave-apply.webp";
import appWorktime from "../../../assets/images/home/product/app-worktime.webp";
import cn from "../../../lib/cn";
import { PhoneFrame } from "./ProductFrames";

const POINTS = [
  {
    icon: Fingerprint,
    title: "Check in with a selfie",
    text: "Face capture inside the office geo-fence.",
  },
  {
    icon: Timer,
    title: "Know where the day stands",
    text: "Work time, breaks and overtime, live.",
  },
  {
    icon: CalendarDays,
    title: "Leave in a few taps",
    text: "Pick the dates, add a reason, done.",
  },
  {
    icon: Inbox,
    title: "Everything else, close at hand",
    text: "Payslips, approvals and requests.",
  },
];

// Staggered heights give the row some rhythm without tilting the screens.
const SCREENS = [
  { src: appHome, alt: "HRx app home screen", offset: "lg:mt-16" },
  { src: appCheckIn, alt: "HRx app selfie check-in", offset: "lg:mt-0" },
  { src: appWorktime, alt: "HRx app work time summary", offset: "lg:mt-24" },
  { src: appLeaveApply, alt: "HRx app leave application", offset: "lg:mt-8" },
];

const MobileApp = () => (
  <section className="relative overflow-hidden border-t border-gray-100 bg-gradient-to-b from-gray-50/80 to-white px-4 py-24 sm:px-6 sm:py-28 lg:px-8">
    <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[minmax(0,4fr)_minmax(0,7fr)] lg:gap-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        <p className="text-sm font-semibold uppercase tracking-wider text-theme-primary">
          Employee app
        </p>
        <h2 className="mt-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          HR your team can carry in their pocket
        </h2>
        <p className="mt-4 text-lg leading-relaxed text-gray-500">
          The admin panel keeps HR in control. The app gives every employee
          what they need, without a single email to HR.
        </p>

        <dl className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-1">
          {POINTS.map(({ icon: Icon, title, text }) => (
            <div key={title} className="flex gap-3.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-theme-primary">
                <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
              </span>
              <div>
                <dt className="text-[15px] font-semibold text-gray-900">
                  {title}
                </dt>
                <dd className="mt-0.5 text-sm leading-6 text-gray-500">
                  {text}
                </dd>
              </div>
            </div>
          ))}
        </dl>
      </motion.div>

      <div className="relative">
        <div className="pointer-events-none absolute inset-x-0 top-1/2 h-72 -translate-y-1/2 rounded-full bg-theme-primary/15 blur-[100px]" />
        <div className="relative -mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-4 sm:mx-0 sm:grid sm:grid-cols-4 sm:overflow-visible sm:px-0 sm:pb-0 lg:items-start">
          {SCREENS.map((screen, index) => (
            <motion.div
              key={screen.alt}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.7,
                delay: index * 0.08,
                ease: [0.16, 1, 0.3, 1],
              }}
              className={cn("w-[44%] shrink-0 snap-center sm:w-auto", screen.offset)}
            >
              <PhoneFrame src={screen.src} alt={screen.alt} />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default MobileApp;
